import React, { FC, useCallback, useContext } from "react";
import { CartItem } from "@pepdirect/v3/ui/CartItem";
import { Divider } from "@pepdirect/v3/ui/Divider";
import { ItemErrorMsg, ItemWithErrorMsgs } from "@pepdirect/v3/types";
import { LocalizationContext } from "v3/context/localization";
import {
  BundleComponent,
  DiscountType,
  LineItemDiscount,
  LineItemFragment,
} from "v3/services/graphql/generated";
import { StyledList } from "./index.styled";

interface CartItemListProps {
  itemErrors?: ItemWithErrorMsgs[];
  lineItems: LineItemFragment[];
}

export const LineItemList: FC<CartItemListProps> = ({
  itemErrors,
  lineItems,
}) => {
  const { formatPriceByLocale, localization } = useContext(LocalizationContext);
  const componentsLocalization = localization?.components;
  const orderSummaryLocalization = localization?.checkout?.orderSummary;

  const getErrorMsgs = useCallback(
    (id: string) => {
      const errors = itemErrors?.find((err) => err.id === id);
      if (errors) return errors.errorMsgs;
    },
    [itemErrors]
  );

  const getBundleErrorMsg = useCallback(
    (bundleItems: BundleComponent[]): ItemErrorMsg[] => {
      const hasErrors = bundleItems.some((bundleItem) =>
        bundleItem?.componentPriceItems?.some((priceItem) =>
          getErrorMsgs(priceItem?.itemId)
        )
      );
      if (hasErrors)
        return [
          {
            alertType: "error",
            title: "Unavailable",
            note: "Please remove this bundle to proceed.",
          },
        ];
      return [];
    },
    [getErrorMsgs]
  );

  const formattedDiscounts = (discounts: LineItemDiscount[]) => {
    return discounts.map(({ amount, category, type }) => ({
      amount: formatPriceByLocale(
        amount,
        type === DiscountType.Percent ? "percent" : "currency"
      ),
      category,
    }));
  };

  return (
    <StyledList>
      {lineItems.map((lineItem, i) => {
        if (lineItem.__typename === "BundleLineItem") {
          const {
            bundleComponents,
            discounts,
            imgUrl,
            itemNotifications,
            perUnitPrice,
            primaryComponent,
            strikethroughUnitPrice,
            subtotal,
            title,
          } = lineItem;

          const allBundleItems = [
            ...(primaryComponent ? [primaryComponent] : []),
            ...(bundleComponents || []),
          ] as BundleComponent[];

          const primaryGtin =
            primaryComponent.componentPriceItems?.[0]?.item.gtin || "";

          return (
            <div key={`line-item-${i}`}>
              <CartItem
                bundleItems={allBundleItems}
                discounts={formattedDiscounts(discounts)}
                errorMsgs={getBundleErrorMsg(allBundleItems)}
                gtin={primaryGtin}
                imgUrl={imgUrl}
                itemNotifications={itemNotifications}
                perUnitPrice={formatPriceByLocale(perUnitPrice)}
                quantity={1}
                strikethroughUnitPrice={formatPriceByLocale(
                  strikethroughUnitPrice
                )}
                subtotal={formatPriceByLocale(subtotal)}
                title={title}
                componentsLocalization={componentsLocalization}
                orderSummaryLocalization={orderSummaryLocalization}
              />
              <Divider />
            </div>
          );
        } else if (lineItem.__typename === "LineItem") {
          const {
            customizations,
            discounts,
            gtin,
            imgUrl,
            itemId,
            itemNotifications,
            perUnitPrice,
            quantity,
            strikethroughUnitPrice,
            subscription,
            subtotal,
            title,
          } = lineItem;

          return (
            <div key={`line-item-${i}`}>
              <CartItem
                customizations={customizations}
                discounts={formattedDiscounts(discounts)}
                errorMsgs={getErrorMsgs(itemId)}
                gtin={gtin}
                imgUrl={imgUrl}
                itemNotifications={itemNotifications}
                perUnitPrice={formatPriceByLocale(perUnitPrice)}
                quantity={quantity}
                strikethroughUnitPrice={formatPriceByLocale(
                  strikethroughUnitPrice
                )}
                subscription={subscription}
                subtotal={formatPriceByLocale(subtotal)}
                title={title}
                componentsLocalization={componentsLocalization}
                orderSummaryLocalization={orderSummaryLocalization}
              />
              <Divider />
            </div>
          );
        }
      })}
    </StyledList>
  );
};
